import { ChangeEvent, FC, memo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import BaseModal from 'components/BaseModal'
import { TransactionButton } from 'components/Button/Button'
import { notifyError } from 'components/ErrorNotifier'
import Input from 'components/Input/Input'
import { FlexDivRow } from 'components/layout/flex'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { setOpenModal } from 'state/app/reducer'
import { addDelegate } from 'state/futures/actions'
import { setNewNickname } from 'state/futures/reducer'
import {
	selectDelegatesList,
	selectNewNickname,
	selectSelectedDelegationAddress,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'

import NewDelegationHeader from './NewDelegationHeader'

type Props = {
	onDismiss(): void
}

const AddDelegateModal: FC<Props> = memo(({ onDismiss }) => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const delegatesList = useAppSelector(selectDelegatesList)
	const selectedAddress = useAppSelector(selectSelectedDelegationAddress)
	const newNickname = useAppSelector(selectNewNickname)

	useEffect(() => {
		dispatch(setNewNickname(''))
	}, [dispatch])

	const handleNewDelegation = useCallback(() => {
		const isAddressExist = delegatesList.some(({ address }) => address === selectedAddress)
		if (!isAddressExist) {
			try {
				dispatch(addDelegate())
				dispatch(setOpenModal('manage_delegates'))
			} catch (error) {
				notifyError('Failed to add new delegate', error)
			}
		} else {
			notifyError('This address already exists in your list')
		}
	}, [delegatesList, dispatch, selectedAddress])

	const onChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => dispatch(setNewNickname(e.target.value)),
		[dispatch]
	)

	return (
		<StyledBaseModal title={<NewDelegationHeader />} isOpen onDismiss={onDismiss}>
			<Spacer height={30} />
			<FlexDivRow>
				<Body size="small" color="secondary">
					{t('futures.market.trade.margin.modal.delegation.new.input-label')}
				</Body>
				<Body size="small" color="secondary">
					{t('futures.market.trade.margin.modal.delegation.new.optional')}
				</Body>
			</FlexDivRow>
			<Spacer height={10} />
			<NickNameInput
				placeholder={t('futures.market.trade.margin.modal.delegation.new.placeholder')}
				value={newNickname}
				onChange={onChange}
			/>
			<Spacer height={20} />
			<MessageContainer>
				<TextList>
					<li>{t('futures.market.trade.margin.modal.delegation.new.warning-copy-1')}</li>
					<li>{t('futures.market.trade.margin.modal.delegation.new.warning-copy-2')}</li>
				</TextList>
			</MessageContainer>
			<Spacer height={30} />
			<TransactionButton size="small" fullWidth onClick={handleNewDelegation}>
				{t('futures.market.trade.margin.modal.delegation.new.create-delegate-button')}
			</TransactionButton>
		</StyledBaseModal>
	)
})

const NickNameInput = styled(Input)`
	font-size: 13px;
`

const MessageContainer = styled.div`
	color: ${(props) => props.theme.colors.selectedTheme.button.yellow.text};
	flex: none;
	order: 0;
	flex-grow: 0;
	text-align: left;
	font-size: 13px;
	padding: 10px;
	border: 1px solid rgba(239, 104, 104, 0);
	background: ${(props) => props.theme.colors.selectedTheme.button.yellow.fill};
	border-radius: 8px;
	cursor: default;
	white-space: pre-line;
`

const TextList = styled.ul`
	list-style-position: inside;
	list-style: initial;
	margin: initial;
	padding: 10px 20px;
	li:not(:last-child) {
		margin-bottom: 10px;
	}
`

const StyledBaseModal = styled(BaseModal)`
	[data-reach-dialog-content] {
		width: 400px;
		margin-top: 300px;
	}
`

export default AddDelegateModal
